/**
 * Readiness Audit Page
 * ====================
 * Live validation of operational inputs & blockers.
 * Checks parks, contractors, clients, finance, SLAs, and job data quality.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Building, Users, Briefcase, CreditCard, Clock, FileText,
  ChevronDown, ChevronUp, Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import GuidedFixPanel from "../components/GuidedFixPanel";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Traffic light badge
const Badge = ({ traffic }) => {
  const styles = {
    green: "bg-green-100 text-green-700 border-green-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${styles[traffic] || styles.red}`}>
      {traffic?.toUpperCase() || "UNKNOWN"}
    </span>
  );
};

// Score card component
const ScoreCard = ({ title, icon: Icon, score, traffic, children, issues = [], expanded, onToggle }) => {
  const hasIssues = issues && issues.length > 0;
  
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              traffic === "green" ? "bg-green-100" : 
              traffic === "amber" ? "bg-amber-100" : "bg-red-100"
            }`}>
              <Icon className={`h-5 w-5 ${
                traffic === "green" ? "text-green-600" : 
                traffic === "amber" ? "text-amber-600" : "text-red-600"
              }`} />
            </div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          <Badge traffic={traffic} />
        </div>
        
        <div className="flex items-end justify-between">
          <div className="text-4xl font-bold text-slate-900">{score}%</div>
          <div className="text-sm text-slate-500">{children}</div>
        </div>
      </div>
      
      {hasIssues && (
        <div className="border-t border-slate-100">
          <button
            onClick={onToggle}
            className="w-full px-5 py-3 flex items-center justify-between text-sm text-slate-600 hover:bg-slate-50"
          >
            <span>{issues.length} issue{issues.length !== 1 ? "s" : ""} found</span>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          {expanded && (
            <div className="px-5 pb-4 max-h-48 overflow-y-auto space-y-2">
              {issues.map((issue, idx) => (
                <div key={idx} className="text-sm p-2 bg-slate-50 rounded-lg">
                  <div className="font-medium text-slate-700">
                    {issue.name || issue.company || issue.job_id || `Item ${idx + 1}`}
                  </div>
                  <div className="text-slate-500 text-xs mt-1">
                    Missing: {issue.missing?.join(", ") || "Unknown"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Checklist card for finance/SLA checks
const ChecklistCard = ({ title, icon: Icon, score, traffic, checks }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            traffic === "green" ? "bg-green-100" : 
            traffic === "amber" ? "bg-amber-100" : "bg-red-100"
          }`}>
            <Icon className={`h-5 w-5 ${
              traffic === "green" ? "text-green-600" : 
              traffic === "amber" ? "text-amber-600" : "text-red-600"
            }`} />
          </div>
          <h3 className="font-semibold text-slate-800">{title}</h3>
        </div>
        <Badge traffic={traffic} />
      </div>
      
      <div className="text-4xl font-bold text-slate-900 mb-4">{score}%</div>
      
      <div className="space-y-2">
        {Object.entries(checks || {}).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2 text-sm">
            {value ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400" />
            )}
            <span className={value ? "text-slate-700" : "text-slate-400"}>
              {key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ReadinessAuditPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const loadAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/readiness/audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load audit:", err);
      setError(err.response?.data?.detail || "Failed to load readiness audit");
      toast.error("Failed to load readiness audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudit();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-3" />
          <p className="text-slate-600">Running readiness audit...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Audit Failed</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <Button onClick={loadAudit}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { blocks, overall_score, traffic } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="readiness-audit-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white p-6 rounded-3xl shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">System Readiness Audit</h1>
              <p className="text-teal-100 mt-1">
                Live validation of operational inputs & blockers
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-4xl sm:text-5xl font-bold">{overall_score}%</div>
                <Badge traffic={traffic} />
              </div>
              <Button 
                onClick={loadAudit} 
                variant="outline" 
                size="sm"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Overall Progress Bar */}
          <div className="mt-6 h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                traffic === "green" ? "bg-green-400" :
                traffic === "amber" ? "bg-amber-400" : "bg-red-400"
              }`}
              style={{ width: `${overall_score}%` }}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={() => navigate("/fm/intake")}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Zap className="h-4 w-4 mr-2" />
            Quick Job Create
          </Button>
          <Button 
            onClick={() => navigate("/contractors")}
            variant="outline"
          >
            <Users className="h-4 w-4 mr-2" />
            Manage Contractors
          </Button>
          <Button 
            onClick={() => navigate("/parks")}
            variant="outline"
          >
            <Building className="h-4 w-4 mr-2" />
            Manage Parks
          </Button>
        </div>

        {/* Score Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Parks */}
          <ScoreCard
            title="Parks / Sites"
            icon={Building}
            score={blocks.parks?.score || 0}
            traffic={blocks.parks?.traffic || "red"}
            issues={blocks.parks?.issues || []}
            expanded={expandedSections.parks}
            onToggle={() => toggleSection("parks")}
          >
            {blocks.parks?.complete || 0}/{blocks.parks?.total || 0} complete
          </ScoreCard>

          {/* Contractors */}
          <ScoreCard
            title="Contractors"
            icon={Users}
            score={blocks.contractors?.score || 0}
            traffic={blocks.contractors?.traffic || "red"}
            issues={blocks.contractors?.issues || []}
            expanded={expandedSections.contractors}
            onToggle={() => toggleSection("contractors")}
          >
            {blocks.contractors?.complete || 0}/{blocks.contractors?.total || 0} complete
          </ScoreCard>

          {/* Clients */}
          <ScoreCard
            title="Clients"
            icon={Briefcase}
            score={blocks.clients?.score || 0}
            traffic={blocks.clients?.traffic || "red"}
            issues={blocks.clients?.issues || []}
            expanded={expandedSections.clients}
            onToggle={() => toggleSection("clients")}
          >
            {blocks.clients?.complete || 0}/{blocks.clients?.total || 0} complete
          </ScoreCard>

          {/* Finance Setup */}
          <ChecklistCard
            title="Finance Setup"
            icon={CreditCard}
            score={blocks.finance?.score || 0}
            traffic={blocks.finance?.traffic || "red"}
            checks={blocks.finance?.checks || {}}
          />

          {/* SLAs */}
          <ChecklistCard
            title="SLA Rules"
            icon={Clock}
            score={blocks.slas?.score || 0}
            traffic={blocks.slas?.traffic || "red"}
            checks={blocks.slas?.checks || {}}
          />

          {/* Job Quality */}
          <ScoreCard
            title="Job Data Quality (7d)"
            icon={FileText}
            score={blocks.job_quality?.score || 0}
            traffic={blocks.job_quality?.traffic || "red"}
            issues={blocks.job_quality?.issues || []}
            expanded={expandedSections.job_quality}
            onToggle={() => toggleSection("job_quality")}
          >
            {blocks.job_quality?.complete || 0}/{blocks.job_quality?.last_7_days || 0} valid
          </ScoreCard>
        </div>

        {/* Intake System Status */}
        {blocks.intake_system && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Smart Intake System</h3>
                  <p className="text-sm text-slate-500">Job creation APIs and configuration</p>
                </div>
              </div>
              <Badge traffic={blocks.intake_system.traffic} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {Object.entries(blocks.intake_system.checks || {}).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  {value ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  )}
                  <span className="text-slate-600 truncate">
                    {key.replace(/_/g, " ").replace("api", "API")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Guided Fix Panel */}
        <GuidedFixPanel onFixApplied={loadAudit} />

        {/* Top Issues Summary */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Top Issues Requiring Attention
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 max-h-64 overflow-y-auto">
            {/* Contractor Issues */}
            {blocks.contractors?.issues?.slice(0, 3).map((issue, idx) => (
              <div key={`contractor-${idx}`} className="p-3 bg-red-50 rounded-xl border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="font-medium text-red-800">Contractor</span>
                </div>
                <div className="text-sm text-red-700">{issue.company}</div>
                <div className="text-xs text-red-500 mt-1">
                  Missing: {issue.missing?.join(", ")}
                </div>
              </div>
            ))}
            
            {/* Park Issues */}
            {blocks.parks?.issues?.slice(0, 3).map((issue, idx) => (
              <div key={`park-${idx}`} className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="font-medium text-amber-800">Park/Site</span>
                </div>
                <div className="text-sm text-amber-700">{issue.name}</div>
                <div className="text-xs text-amber-500 mt-1">
                  Missing: {issue.missing?.join(", ")}
                </div>
              </div>
            ))}
            
            {/* Job Quality Issues */}
            {blocks.job_quality?.issues?.slice(0, 2).map((issue, idx) => (
              <div key={`job-${idx}`} className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <span className="font-medium text-slate-700">Job</span>
                </div>
                <div className="text-sm text-slate-600">{issue.job_id}</div>
                <div className="text-xs text-slate-400 mt-1">
                  Missing: {issue.missing?.join(", ")}
                </div>
              </div>
            ))}
            
            {/* No issues message */}
            {(!blocks.contractors?.issues?.length && 
              !blocks.parks?.issues?.length && 
              !blocks.job_quality?.issues?.length) && (
              <div className="col-span-2 text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-slate-600">No critical issues found!</p>
              </div>
            )}
          </div>
        </div>

        {/* Generated timestamp */}
        <div className="text-center text-sm text-slate-400">
          Audit generated at {new Date(data.generated_at).toLocaleString("en-GB")}
        </div>
      </div>
    </div>
  );
}
